import { useCallback } from "react";
import { useAtomValue } from "jotai";
import { Button } from "@common/Button";
import { ComponentColor } from "@common/types";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { isProcessingLocationsAtom } from "@/store/locationAtoms";
import type { LocationResponse } from "@/models/location";
import { wasReported } from "@utils/apiFailure";

export const DELETE_LOCATION_MODAL_ID = "accountDeleteLocationModal";

interface DeleteLocationModalProps {
    location?: LocationResponse;
    /** After a successful delete: the list re-reads, the detail page goes back to the list. */
    onDeleted: () => void;
    onClose?: () => void;
}

export const DeleteLocationModal = ({
    location,
    onDeleted,
    onClose,
}: DeleteLocationModalProps) => {
    const { deleteLocation } = useLocationApi();
    const isProcessing = useAtomValue(isProcessingLocationsAtom);

    const handleClose = useCallback(() => {
        const modal = document.getElementById(
            DELETE_LOCATION_MODAL_ID,
        ) as HTMLDialogElement | null;
        modal?.close();
        onClose?.();
    }, [onClose]);

    const handleDelete = async () => {
        if (!location) return;
        try {
            await deleteLocation(location.id);
        } catch (error) {
            // useApi has shown this one: the dialog stays open for a retry or
            // a cancel. Any other failure was shown to nobody, so it escapes.
            if (wasReported(error)) return;
            throw error;
        }
        onDeleted();
        handleClose();
    };

    return (
        <dialog
            id={DELETE_LOCATION_MODAL_ID}
            className="modal text-neutral"
            onCancel={(e) => {
                if (isProcessing) {
                    e.preventDefault();
                    return;
                }
                // Esc closes the dialog natively; clear the parent's selected-row state too.
                onClose?.();
            }}
        >
            <div className="modal-box">
                <h1 className="font-bold text-2xl mb-4">Delete Location</h1>
                <p className="mb-6">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold">{location?.name}</span>? This
                    can&apos;t be undone.
                </p>

                <div className="flex gap-2 justify-end">
                    <Button
                        color={ComponentColor.Error}
                        onClick={handleDelete}
                        disabled={isProcessing}
                        loading={isProcessing}
                    >
                        Delete
                    </Button>
                    <Button
                        color={ComponentColor.Neutral}
                        onClick={handleClose}
                        disabled={isProcessing}
                    >
                        Cancel
                    </Button>
                </div>
            </div>

            {!isProcessing && (
                <form method="dialog" className="modal-backdrop">
                    <button type="submit" onClick={handleClose}>
                        close
                    </button>
                </form>
            )}
        </dialog>
    );
};
